import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { lookupPayoutRecipient, payThroughPaybill } from '../../../../services/AccountService';
import PayoutRecipientConfirmationModal from './PayoutRecipientConfirmationModal';
import ExpenseTagsPicker from './ExpenseTagsPicker';
import { readErrorEnvelope } from '../../../../utils/errorEnvelope';
import { toast } from 'react-toastify';
import PinInput from '../../PinInput';

const BuyGoodsForm = ({
  expenseTags,
  expenseTagsLoading,
  userWalletBalance,
  userId,
  userWalletBalanceLoading,
  onTransactionSubmitted,
}) => {

  const [selectedTags, setSelectedTags] = useState([]);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [pinAttemptsRemaining, setPinAttemptsRemaining] = useState(null);
  const [pinLockRetryAfterSeconds, setPinLockRetryAfterSeconds] = useState(null);
  const [formData, setFormData] = useState({
    buyGoodsTillNumber: '',
    buyGoodsTillAmount: '',
    transactionPin: '',
  });
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [verifiedRecipientName, setVerifiedRecipientName] = useState(null);
  const [pendingPayload, setPendingPayload] = useState(null);

  const payThroughPaybillMutation = useMutation({
    mutationFn: (payload) => payThroughPaybill(userId, payload),
  });

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handlePayViaBuyGoods = async (e) => {
    e.preventDefault();

    if (paymentProcessing) return;
    setPaymentProcessing(true);

    const selectedTagIds = (expenseTags || [])
      .filter(tag => selectedTags.includes(tag.name))
      .map(tag => tag.id);

    const payload = {
      PartyB: formData.buyGoodsTillNumber,
      Amount: formData.buyGoodsTillAmount,
      CommandID: 'BusinessBuyGoods',
      ReceiverIdentifierType: 2,
      expense_tag_ids: selectedTagIds,
      transaction_pin: formData.transactionPin
    };

    setPendingPayload(payload);

    try {
      const response = await lookupPayoutRecipient(userId, 'till', formData.buyGoodsTillNumber);
      setVerifiedRecipientName(response.data.receiver_name);
    } catch {
      setVerifiedRecipientName(null);
    }

    setShowConfirmationModal(true);
  };

  const handleModalCancel = () => {
    setShowConfirmationModal(false);
    setPaymentProcessing(false);
    setPendingPayload(null);
    setVerifiedRecipientName(null);
  };

  const handleModalConfirm = async () => {
    try {
      await toast.promise(
        payThroughPaybillMutation.mutateAsync(pendingPayload),
        {
          pending: 'Processing request ...',
          success: 'Your request will be processed shortly.',
          error: {
            render({ data: err }) {
              return readErrorEnvelope(err).message;
            }
          }
        }
      );
      onTransactionSubmitted();
      resetForm();
    } catch (err) {
      const { errorCode, meta } = readErrorEnvelope(err);
      if (errorCode === 'PIN_INVALID') {
        setPinAttemptsRemaining(meta.remaining_attempts ?? null);
        setPinLockRetryAfterSeconds(null);
      } else if (errorCode === 'PIN_LOCKED') {
        setPinLockRetryAfterSeconds(meta.retry_after_seconds ?? null);
        setPinAttemptsRemaining(null);
      }
    } finally {
      setShowConfirmationModal(false);
      setPaymentProcessing(false);
      setPendingPayload(null);
      setVerifiedRecipientName(null);
    }
  };

  const resetForm = () => {
    setFormData({
      buyGoodsTillNumber: '',
      buyGoodsTillAmount: '',
      transactionPin: '',
    });
    setSelectedTags([]);
    setPinAttemptsRemaining(null);
    setPinLockRetryAfterSeconds(null);
  }

  const isAmountBelowMinimum = formData.buyGoodsTillAmount !== '' && Number(formData.buyGoodsTillAmount) < 5;

  const areAllFieldsFilled = () => {
    return formData.buyGoodsTillNumber &&
      formData.buyGoodsTillAmount &&
      Number(formData.buyGoodsTillAmount) >= 5 &&
      selectedTags.length > 0;
  }

  return (
    <>
      {/* <ToastContainer /> */}
      <form onSubmit={handlePayViaBuyGoods}>
        <div className="sell-blance">
          <div className="form-label blance"><span>BALANCE:</span>
            {userWalletBalanceLoading ? (
              <span className="spinner-border spinner-border-sm text-primary ms-2" role="status" />
            ) : (
              <p>{userWalletBalance}</p>
            )}
          </div>
          <label className="form-label text-primary" htmlFor='buyGoodsTillNumber'>Till Number</label>

          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder=""
              id='buyGoodsTillNumber'
              value={formData.buyGoodsTillNumber}
              onChange={handleInputChange}
            />
          </div>
        </div>
        <div className="sell-blance">
          <label className="form-label text-primary" htmlFor='buyGoodsTillAmount'>Amount</label>
          <div className="input-group">
            <input
              type="number"
              className="form-control"
              placeholder=""
              id='buyGoodsTillAmount'
              min="5"
              step="1"
              value={formData.buyGoodsTillAmount}
              onChange={handleInputChange}
            />
          </div>
          {isAmountBelowMinimum && (
            <small className="text-danger mt-1 d-block">Minimum amount is KES 5.</small>
          )}
        </div>
        <div className="sell-blance">
          {expenseTagsLoading ? (
            <>
              <label className="form-label text-muted small">Expense Tag (select many):</label>
              <span className="spinner-border spinner-border-sm text-primary ms-2" role="status" />
            </>
          ) : (
            <ExpenseTagsPicker
              tags={expenseTags}
              selectedNames={selectedTags}
              onChange={setSelectedTags}
            />
          )}
        </div>

        {areAllFieldsFilled() &&
          (
            <div className="sell-blance mt-3">
              <label className="form-label text-danger" htmlFor="transactionPin">
                Enter Transaction PIN
              </label>
              <div className="input-group">
                <PinInput
                  placeholder="Enter your PIN"
                  id="transactionPin"
                  name="transactionPin"
                  value={formData.transactionPin}
                  onChange={handleInputChange}
                />
              </div>
              {pinAttemptsRemaining !== null && (
                <small className={`mt-1 d-block ${pinAttemptsRemaining <= 1 ? 'text-danger' : 'text-warning'}`}>
                  {pinAttemptsRemaining === 0
                    ? 'No attempts remaining.'
                    : `${pinAttemptsRemaining} attempt${pinAttemptsRemaining === 1 ? '' : 's'} remaining.`}
                </small>
              )}
              {pinLockRetryAfterSeconds !== null && (
                <small className="mt-1 d-block text-danger">
                  Your PIN is locked. Try again in {Math.ceil(pinLockRetryAfterSeconds / 60)} minute{Math.ceil(pinLockRetryAfterSeconds / 60) === 1 ? '' : 's'}.
                </small>
              )}
            </div>
          )}
        <div className="text-center">
          <button
            className="btn btn-primary w-75"
            disabled={
              !formData.transactionPin ||
              !formData.buyGoodsTillNumber ||
              !formData.buyGoodsTillAmount ||
              Number(formData.buyGoodsTillAmount) < 5 ||
              selectedTags.length === 0 ||
              paymentProcessing
            }
          >
            {paymentProcessing ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Processing
              </>
            ) : 'Pay'}
          </button>
        </div>

      </form>

      <PayoutRecipientConfirmationModal
        isOpen={showConfirmationModal}
        recipientName={verifiedRecipientName}
        destination={`Till ${formData.buyGoodsTillNumber}`}
        amount={formData.buyGoodsTillAmount}
        onConfirm={handleModalConfirm}
        onCancel={handleModalCancel}
      />
    </>
  )
}
export default BuyGoodsForm;
